import { SLA_HOURS } from '@ensemble.music/core'
import {
  BadgeCheck,
  CalendarCheck,
  FileText,
  Handshake,
  Inbox,
  type LucideIcon,
  PencilLine,
  UserRound,
  Zap,
} from 'lucide-react'

// One typed place for every piece of landing-page copy and every flag that decides whether a
// block renders at all. docs/CONTENT_TODO.md lists what is still open and who owns it.
// Types instead of a Zod schema on purpose: this is a compile-time literal, so tsc already
// rejects a typo or a missing field, and content.test.ts is what guards the honesty rules
// (no "<<PLACEHOLDER>>" reaching a production render, no invented numbers).

export const BRAND_NAME = 'ensemble'
/** Already published in the Impressum, so it is a real address and may render. */
export const CONTACT_EMAIL = 'info@ensemblemusic.online'
export const REGION = 'Stuttgart und Umgebung'

/** Off until real testimonials, logos or counts exist. Never fake proof. */
export const SHOW_PROOF = false
/** Off until the owner confirms at least three ensembles with approved fixed packages (PR4d). */
export const INSTANT_BOOKING_ENABLED = false
/** null until the owner decides the fee model; the "Buchung" price card stays hidden meanwhile. */
export const PRICING: { label: string; bullets: readonly string[] } | null = null

export type NavSection = { id: string; label: string }

// Only sections that actually exist on the page. "Preise" and "Beispiele" join this list with
// their sections - a nav link to a missing anchor is a bug, not a placeholder.
export const navSections = [
  { id: 'so-funktionierts', label: "So funktioniert's" },
  { id: 'anlaesse', label: 'Anlässe' },
  { id: 'vorteile', label: 'Vorteile' },
  // hidden with the section itself (owner, 2026-10-20)
  // { id: 'referenzen', label: 'Referenzen' },
  { id: 'fragen', label: 'Fragen' },
] as const satisfies readonly NavSection[]

export const hero = {
  overline: `Live-Musik in ${REGION}`,
  headline: `Live-Musik für deine Feier. Drei passende Angebote in ${SLA_HOURS} Stunden.`,
  subline:
    'Sag uns Datum, Ort und Budget. Wir finden verfügbare Ensembles in Stuttgart und Umgebung, du vergleichst Festpreise und buchst mit einer Antwort.',
  primaryCta: 'Anfrage starten – dauert 2 Minuten',
  secondaryCta: "So funktioniert's",
  checks: ['Kostenlos anfragen', 'Festpreise statt Rätselraten', 'Persönliche Betreuung'],
} as const

/** Chips above the form. The value is what lands in the form's free-text "Anlass" field. */
export const occasions = ['Hochzeit', 'Firmenfeier', 'Geburtstag', 'Empfang', 'Sonstiges'] as const

export type Occasion = (typeof occasions)[number]

/** Fictional on purpose and labelled as such, so nobody mistakes it for a real offer. */
export const exampleOffer = {
  badge: 'Beispiel-Angebot',
  ensembleName: 'Beispiel-Duo',
  tagline: 'Akustik-Duo, Gesang und Gitarre',
  rows: [
    { label: 'Besetzung', value: 'Duo – Gesang und Gitarre' },
    { label: 'Dauer', value: '2 × 45 Minuten' },
    { label: 'Festpreis', value: 'steht im Angebot' },
  ],
  videoCaption: 'Videoausschnitt des Ensembles',
  availability: 'Verfügbar an deinem Wunschtermin',
  disclaimer: 'Beispiel zur Veranschaulichung – kein echtes Ensemble und kein echtes Angebot.',
} as const

export type Step = { title: string; body: string; icon: LucideIcon }

// Each step leads with an icon beside its title, so the sequence reads at a glance (owner, 2026-10-19).
export const steps = [
  {
    title: 'Anfrage stellen',
    icon: PencilLine,
    body: 'Datum, Ort, Besetzung, Budget. In zwei Minuten erledigt, kostenlos und unverbindlich.',
  },
  {
    title: 'Angebote erhalten',
    icon: Inbox,
    body: `Wir prüfen die Verfügbarkeit und schicken dir innerhalb von ${SLA_HOURS} Stunden bis zu drei passende Ensembles mit Festpreis und Video.`,
  },
  {
    title: 'Buchen',
    icon: CalendarCheck,
    body: 'Du wählst ein Ensemble. Wir bestätigen Termin und Ablauf und kümmern uns um die Abwicklung.',
  },
] as const satisfies readonly Step[]

export type EventType = {
  id: string
  title: string
  blurb: string
  /** Occasion value handed to the request form. */
  occasion: string
  /** Path under /images once a real photo exists; null renders the labelled placeholder tile. */
  image: string | null
}

// The occasions we say yes to. Owner decision 2026-10-18 added Trauerfeier to the four from
// BUSINESS_CASE section 1. Photos are free Unsplash images (owner, 2026-10-18), resized to 900 px
// wide; the card credits Unsplash in a corner.
export const eventTypes = [
  {
    id: 'hochzeit',
    title: 'Hochzeit',
    blurb: 'Trauung, Empfang und Abend – oft drei Stimmungen an einem Tag.',
    occasion: 'Hochzeit',
    image: '/images/landing/wedding-900.jpg',
  },
  {
    id: 'firmenfeier',
    title: 'Firmenfeier',
    blurb: 'Sommerfest, Jubiläum oder Weihnachtsfeier. Planbar und pünktlich.',
    occasion: 'Firmenfeier',
    image: '/images/landing/company-event-900.jpg',
  },
  {
    id: 'party',
    title: 'Party',
    blurb: 'Ein Ensemble, das die Leute auf die Tanzfläche holt und dort hält.',
    occasion: 'Party',
    image: '/images/landing/dj-party-900.jpg',
  },
  {
    id: 'geburtstag',
    title: 'Geburtstagsfeier',
    blurb: 'Vom leisen Duo beim Essen bis zum Ensemble für den runden Geburtstag.',
    occasion: 'Geburtstagsfeier',
    image: '/images/landing/birthday-party-900.jpg',
  },
  {
    id: 'empfang',
    title: 'Empfang',
    blurb: 'Hintergrundmusik, die Gespräche trägt statt sie zu übertönen.',
    occasion: 'Empfang',
    image: '/images/landing/jazz-combo-900.jpg',
  },
  {
    id: 'trauerfeier',
    title: 'Trauerfeier',
    blurb: 'Zurückhaltende Besetzungen und Stücke, die zur Feier passen.',
    occasion: 'Trauerfeier',
    image: '/images/landing/funeral-orgean-900.jpg',
  },
] as const satisfies readonly EventType[]

export type Benefit = { title: string; body: string; icon: LucideIcon }

// Six claims we can stand behind today. "Vergleichbar" and "Transparent" from the page brief are
// missing on purpose: both need an owner confirmation that has not happened (CONTENT_TODO.md).
export const benefits = [
  {
    title: 'Schnell',
    icon: Zap,
    body: `Drei Angebote in ${SLA_HOURS} Stunden statt Wochen Hin und Her.`,
  },
  {
    title: 'Klare Angebote',
    icon: FileText,
    body: 'Du siehst Besetzung, Dauer und Festpreis, bevor du dich entscheidest.',
  },
  {
    title: 'Verfügbar',
    icon: CalendarCheck,
    body: 'Wir fragen nur Ensembles an, die an deinem Datum Zeit haben.',
  },
  {
    title: 'Persönlich',
    icon: UserRound,
    body: 'Ein fester Ansprechpartner statt anonymem Portal.',
  },
  {
    title: 'Verlässlich',
    icon: Handshake,
    body: 'Klare Absprachen zu Ablauf und Bestätigung, schriftlich festgehalten.',
  },
  {
    title: 'Geprüft',
    icon: BadgeCheck,
    body: 'Wir hören jedes Ensemble selbst, bevor es dir ein Angebot machen darf.',
  },
] as const satisfies readonly Benefit[]

export type Reference = { quote: string; person: string; occasion: string }

/**
 * Only real, approved quotes belong in here - never an invented one, not even as a sample. The
 * list is empty until the first booker has agreed in writing, and SHOW_PROOF keeps even a filled
 * list off the production page until the owner releases it. While it is empty the section renders
 * the placeholder slots below, which say exactly that. See docs/CONTENT_TODO.md.
 */
export const references: readonly Reference[] = []

/**
 * What the quiet Referenzen strip shows until real quotes or venue logos exist: a short honest
 * line and named empty slots. No headline, no cards - the strip is not a section of its own.
 */
export const referencesPlaceholder = {
  label: 'Referenzen',
  note: 'Wir sind gerade gestartet. Hier stehen bald Rückmeldungen und Veranstaltungsorte – mit Namen, Anlass und Einverständnis.',
  /** One slot per occasion we expect the first references from. */
  slots: ['Hochzeit', 'Firmenfeier', 'Geburtstagsfeier'],
} as const

export type FaqItem = { q: string; a: string }

export const faq = [
  {
    q: 'Wie schnell bekomme ich Angebote?',
    a: `In der Regel innerhalb von ${SLA_HOURS} Stunden nach deiner Anfrage. Wenn wir für deinen Termin kein passendes Ensemble finden, sagen wir das ehrlich und genauso schnell.`,
  },
  {
    q: 'Was kostet die Anfrage?',
    a: 'Nichts. Du zahlst erst, wenn du ein Ensemble buchst. Die Servicegebühr nennen wir dir mit dem Angebot, bevor du zusagst.',
  },
  {
    q: 'Wie viel kostet Live-Musik?',
    a: 'Das hängt von Besetzung, Dauer und Ort ab. Mit dem Angebot siehst du den Festpreis und weißt, was enthalten ist.',
  },
  {
    q: 'Für welche Anlässe könnt ihr Ensembles vermitteln?',
    a: 'Hochzeiten (Trauung, Empfang, Abend), Firmenfeiern, Partys, Geburtstage, Empfänge und Trauerfeiern.',
  },
  {
    q: 'Welche Besetzungen gibt es?',
    a: 'Alles, was wir Ensemble nennen: vom Solo-Künstler und Akustik-Duo über Trauungsmusik und Streichquartett bis zu Partyband und Orchester. Wenn du unsicher bist, beschreibe einfach den Anlass – wir schlagen etwas vor.',
  },
  {
    q: 'In welchen Regionen seid ihr aktiv?',
    a: `Wir starten in ${REGION}. Frag gern an, wenn dein Ort weiter weg liegt – wir sagen dir offen, ob wir jemanden haben.`,
  },
  {
    q: 'Was passiert nach meiner Anfrage?',
    a: 'Wir prüfen die Verfügbarkeit, melden uns bei dir und schicken dir bis zu drei Angebote. Du wählst ein Ensemble, wir bestätigen Termin und Ablauf.',
  },
  {
    q: 'Wie läuft die Bezahlung?',
    a: 'Du erhältst eine Rechnung. Die genauen Konditionen nennen wir dir mit dem Angebot.',
  },
  {
    q: 'Was passiert mit meinen Daten?',
    a: 'Wir nutzen deine Angaben nur, um deine Anfrage zu bearbeiten, und geben sie nur an die Ensembles weiter, die dir ein Angebot machen. Details in der Datenschutzerklärung.',
  },
] as const satisfies readonly FaqItem[]

export const finalCta = {
  headline: 'Bereit für die richtige Musik?',
  body: 'Eine Anfrage, bis zu drei Angebote. Kostenlos und unverbindlich.',
  button: 'Anfrage starten',
} as const
